import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { BellAlertIcon, UserPlusIcon, HeartIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import AuthContext from '../../contexts/AuthContext';
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
  listenToNotifications
} from '../../services/notificationService';

// Definir la interfaz para las notificaciones
interface NotificationItem {
  id: string;
  type: 'follow' | 'like' | 'comment' | 'mention' | 'reply';
  fromUserId: string;
  fromUserName: string;
  fromUserPhoto: string;
  toUserId: string;
  postId?: string;
  commentId?: string;
  content?: string;
  read: boolean;
  createdAt: any; // Timestamp from Firebase
}

const NotificationsList: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    // Configurar un listener para actualizaciones en tiempo real de las notificaciones
    const unsubscribe = listenToNotifications(currentUser.uid, (updatedNotifications) => {
      setNotifications(updatedNotifications as NotificationItem[]);
      setLoading(false);
    });

    // Limpiar el listener cuando el componente se desmonte
    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  const handleMarkAsRead = async (notificationId: string) => {
    if (!currentUser) return;

    try {
      await markNotificationAsRead(currentUser.uid, notificationId);
      // La actualización se reflejará automáticamente a través del listener
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser) return;

    try {
      await markAllNotificationsAsRead(currentUser.uid);
      // La actualización se reflejará automáticamente a través del listener
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / 36e5;

    if (diffInHours < 24) {
      // Si es menos de 24 horas, mostrar "hace X horas" o "hace X minutos"
      if (diffInHours < 1) {
        const minutes = Math.floor(diffInHours * 60);
        return minutes <= 0 ? 'ahora mismo' : `hace ${minutes} min`;
      }
      return `hace ${Math.floor(diffInHours)} h`;
    } else if (diffInHours < 48) {
      // Si es menos de 48 horas, mostrar "ayer"
      return 'ayer';
    } else {
      // Si es más de 48 horas, mostrar la fecha
      return format(date, 'dd MMM', { locale: es });
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'follow':
        return <UserPlusIcon className="h-5 w-5 text-blue-500" />;
      case 'like':
        return <HeartIcon className="h-5 w-5 text-red-500" />;
      case 'comment':
      case 'reply':
        return <ChatBubbleLeftIcon className="h-5 w-5 text-green-500" />;
      case 'mention':
        return <BellAlertIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <BellAlertIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationText = (notification: NotificationItem) => {
    const { type, fromUserName, content } = notification;

    switch (type) {
      case 'follow':
        return `${fromUserName} ha comenzado a seguirte`;
      case 'like':
        return `A ${fromUserName} le ha gustado tu publicación`;
      case 'comment':
        return `${fromUserName} ha comentado en tu publicación: ${content ? `"${content.substring(0, 30)}${content.length > 30 ? '...' : ''}"` : ''}`;
      case 'reply':
        return `${fromUserName} ha respondido a tu comentario: ${content ? `"${content.substring(0, 30)}${content.length > 30 ? '...' : ''}"` : ''}`;
      case 'mention':
        return `${fromUserName} te ha mencionado en una publicación`;
      default:
        return 'Nueva notificación';
    }
  };

  const getNotificationLink = (notification: NotificationItem) => {
    const { type, fromUserId, postId, commentId } = notification;

    switch (type) {
      case 'follow':
        return `/profile/${fromUserId}`;
      case 'like':
      case 'comment':
      case 'mention':
        return postId ? `/post/${postId}` : '/';
      case 'reply':
        return postId && commentId ? `/post/${postId}?comment=${commentId}` : '/';
      default:
        return '/';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-md">
        {error}
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Debes iniciar sesión para ver tus notificaciones.
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No tienes notificaciones.
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Notificaciones</h2>
        {notifications.some(notification => !notification.read) && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm text-domired-600 dark:text-domired-400 hover:text-domired-700 dark:hover:text-domired-300 transition-colors"
          >
            Marcar todas como leídas
          </button>
        )}
      </div>

      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 ${!notification.read ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
          >
            <Link
              to={getNotificationLink(notification)}
              className="flex items-start space-x-3 group"
              onClick={() => !notification.read && handleMarkAsRead(notification.id)}
            >
              <div className="flex-shrink-0">
                <img
                  src={notification.fromUserPhoto || '/default-avatar.png'}
                  alt={notification.fromUserName}
                  className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/default-avatar.png';
                  }}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-domired-600 dark:group-hover:text-domired-400">
                    {getNotificationText(notification)}
                  </p>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    {formatDate(notification.createdAt)}
                  </span>
                </div>
              </div>

              <div className="flex-shrink-0 ml-2">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700">
                    {getNotificationIcon(notification.type)}
                  </span>

                  {!notification.read && (
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-domired-100 dark:bg-domired-900/20">
                      <span className="h-2 w-2 rounded-full bg-domired-600 dark:bg-domired-400"></span>
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationsList;
